import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchTenderData } from '@/api/requests/tenderData';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import { TenderData } from '@/actions/dtos/tender/tender';
import { loadingKey, loadingKeys } from '@/utils/loadingKeys';
import { useLoading } from '@/context/LoadingContext';

interface Options {
  includeEntries?: boolean;
  includeComments?: boolean;
  includeResults?: boolean;
  includeChats?: boolean;
  includeAccesses?: boolean;
  includeVotes?: boolean;
}

export const useTenderData = (tenderIdsOrId: string | string[], options: Options = {}) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tenderIds = useMemo(() => (Array.isArray(tenderIdsOrId) ? tenderIdsOrId : [tenderIdsOrId]), [tenderIdsOrId]);
  const { isAuthenticated } = useAuthenticated();
  const [tenderData, setTenderData] = useState<TenderData[]>([]);
  const { setLoading } = useLoading();
  const idsKey = useMemo(() => tenderIds.join(','), [tenderIds]);

  const fetchData = useCallback(
    async () => {
      if (!isAuthenticated || !tenderIds.length) return;

      const featureKey = loadingKey(loadingKeys.FETCH_TENDER_DATA);
      try {
        setLoading(featureKey, true);
        const data = await fetchTenderData(
          tenderIds,
          options.includeEntries,
          options.includeComments,
          options.includeResults,
          options.includeChats,
          options.includeAccesses,
          options.includeVotes,
        );
        setTenderData(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(featureKey, false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isAuthenticated,
      idsKey,
      options.includeEntries,
      options.includeComments,
      options.includeResults,
      options.includeChats,
      options.includeAccesses,
      options.includeVotes,
    ],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getDataByTenderId = useCallback(
    (tenderId: string) => tenderData.find((t) => t.tender.id === tenderId),
    [tenderData],
  );

  const getEntryByListId = useCallback(
    (tenderId: string, shortlistId: string) =>
      tenderData.find((t) => t.tender.id === tenderId)?.entries.find((e) => e.shortlist.id === shortlistId),
    [tenderData],
  );

  const getVotesByTenderId = useCallback(
    (tenderId: string) => {
      const tenderDataItem = tenderData.find((td) => td.tender.id === tenderId);
      return tenderDataItem?.votes || [];
    },
    [tenderData],
  );

  const refreshSingleTender = useCallback(
    async (tenderId: string) => {
      if (!isAuthenticated) return;

      const featureKey = loadingKey(loadingKeys.REFRESH_SINGLE_TENDER);
      try {
        setLoading(featureKey, true);

        const data = await fetchTenderData(
          [tenderId],
          options.includeEntries,
          options.includeComments,
          options.includeResults,
          options.includeChats,
          options.includeAccesses,
          options.includeVotes,
        );

        setTenderData((prevData) => prevData.map((item) => (item.tender.id === tenderId ? data[0] : item)));
      } finally {
        setLoading(featureKey, false);
      }
    },
    [
      isAuthenticated,
      options.includeEntries,
      options.includeComments,
      options.includeResults,
      options.includeChats,
      options.includeAccesses,
      options.includeVotes,
      setLoading,
    ],
  );

  return {
    tenderData,
    getDataByTenderId,
    getEntryByListId,
    getVotesByTenderId,
    refreshSingleTender,
  };
};
